<script setup lang="ts">
import http from '@/services/http'
import { computed, onBeforeMount, ref } from 'vue'
import { refreshOverviewCallback } from './overviewShared'
import Overview from './Overview.vue'
import type { RiskConfig } from '@/models/api-generated'
import { PermOwnRiskRead, PermAllRiskRead, PermAdminRiskSettings } from '@/models/enums-generated'
import { hasPermission } from '@/services/utils'

const props = defineProps<{ isDashboard: boolean }>()

refreshOverviewCallback.value = fetch

const isLoading = ref(false)
const model = ref<RiskConfig[]>()
const expanded = ref(false)
const canViewRisks = computed(() => hasPermission(PermOwnRiskRead, PermAllRiskRead, PermAdminRiskSettings))
const displayedConfigs = computed(() => {
	if (!model.value) return []
	if (!props.isDashboard || expanded.value || model.value.length <= 3) return model.value
	return model.value.slice(0, 3)
})
const showExpandButton = computed(() => props.isDashboard && model.value && model.value.length > 3)

async function fetch() {
	if (!canViewRisks.value) return
	isLoading.value = true
	const { data } = await http.get('api/risk/configs')
	model.value = data.data
	isLoading.value = false
}

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames?: string[], treatments?: string[]): void
}>()

function handleChildClick(name?: string[], treatment?: string[]) {
	if (props.isDashboard) {
		window.location.href = `/app/risk-management`
		return
	}
	emit('go-to-risks', name, treatment)
}

function toggleExpand() {
	expanded.value = !expanded.value
}

function shouldShowShadow(index: number) {
	return props.isDashboard && !expanded.value && model.value && model.value.length > 3 && index === 2
}

onBeforeMount(async () => {
	fetch()
})
</script>

<template>
	<div class="d-flex align-items-center mb-2">
		<button v-if="showExpandButton" class="btn btn-sm btn-outline-primary me-2" @click="toggleExpand">
			<i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
		</button>
		<p v-if="props.isDashboard" class="fw-bold mb-0">
			{{ $t('risk.risk', 1) }}
			<template v-if="!canViewRisks"> - {{ $t('roles.missingRights') }}</template>
		</p>
	</div>

	<template v-if="model">
		<div v-for="(config, i) in displayedConfigs" :key="i" class="overview-wrapper" :class="{ 'has-more-shadow': shouldShowShadow(i) }">
			<Overview :risk-config="config" @go-to-risks="handleChildClick" :is-dashboard="props.isDashboard" />
			<div v-if="shouldShowShadow(i)" class="overview-fade"></div>
		</div>
	</template>

	<div v-else class="p-4 text-center">
		<div class="spinner-border text-primary"></div>
	</div>
</template>

<style lang="sass" scoped>
.overview-wrapper
  position: relative

.overview-fade
  position: absolute
  left: 0
  bottom: 0
  width: 100%
  height: 4rem
  pointer-events: none
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--bs-body-bg))
</style>
